import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { SectionHeading } from '@/components/generals/section-heading'
import { LiveDot } from '@/components/generals/live-dot'
import { milestones } from '@/constants/company'

/** Horizontal timeline of milestones along a route line that draws itself in. */
export const MilestoneTimeline: FC = (): ReactNode => {
    const ref = useRef<HTMLOListElement>(null)
    const drawn = useInView(ref, { once: true, threshold: 0.2 })

    return (
        <section id='milestones' aria-labelledby='milestones-title' className='section-y'>
            <div className='shell'>
                <SectionHeading
                    id='milestones-title'
                    eyebrow="Milestones"
                    title="Seven years,"
                    accent="one lane at a time."
                    intro="From a weekend fix in a Rotterdam office to 3,400 live connections. Scroll the timeline."
                />
            </div>

            <div className='no-scrollbar mt-12 overflow-x-auto pb-4 mask-fade-x lg:mt-16'>
                <div className='shell'>
                    <ol ref={ref} className='relative flex w-max pt-2'>
                        <span aria-hidden='true' className='absolute left-5 right-10 top-[27px] h-px bg-ink/15' />

                        <span
                            aria-hidden='true'
                            className='absolute left-5 right-10 top-[27px] h-px origin-left bg-ultramarine transition-transform duration-[2600ms] ease-calm'
                            style={{ transform: `scaleX(${drawn ? 1 : 0})` }}
                        />

                        {milestones.map((milestone, index) => {
                            const latest = index === milestones.length - 1

                            return (
                                <li key={milestone.year} className='relative w-[230px] shrink-0 pr-8 sm:w-[260px]'>
                                    <span
                                        className={cn(
                                            'relative z-10 grid size-10 place-items-center rounded-full border bg-paper transition-colors duration-700',
                                            drawn ? 'border-ultramarine/40' : 'border-ink/15'
                                        )}
                                        style={{ transitionDelay: `${index * 280}ms` }}
                                    >
                                        {latest ? (
                                            <LiveDot />
                                        ) : (
                                            <span className={cn('size-2.5 rounded-full transition-colors duration-700', drawn ? 'bg-ultramarine' : 'bg-ink/40')} style={{ transitionDelay: `${index * 280}ms` }} />
                                        )}
                                    </span>

                                    <p className='eyebrow mt-6 text-ultramarine'>
                                        {milestone.year}
                                    </p>

                                    <h3 className='mt-2 font-heading text-lg font-semibold tracking-[-0.02em] text-ink'>
                                        {milestone.title}
                                    </h3>

                                    <p className='mt-2 text-[14px] leading-relaxed text-ink-soft'>
                                        {milestone.text}
                                    </p>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>
        </section>
    )
}
